// Sprint 1 — Enterprise Sales Portal frontend
// All UI for /portal/* and /admin/sales/* lives here.
// Deliberately kept in one file to limit App.js churn.

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Briefcase, LogOut, Pencil, Trash2, Plus, X, Loader2, PoundSterling, Target } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/useSeo";

const STAGES = [
  { key: "new", label: "New lead", color: "bg-slate-100 text-slate-700" },
  { key: "contacted", label: "Contacted", color: "bg-blue-50 text-blue-700" },
  { key: "demo_booked", label: "Demo booked", color: "bg-indigo-50 text-indigo-700" },
  { key: "proposal_sent", label: "Proposal sent", color: "bg-violet-50 text-violet-700" },
  { key: "negotiation", label: "Negotiation", color: "bg-amber-50 text-amber-800" },
  { key: "won", label: "Won", color: "bg-emerald-100 text-emerald-800" },
  { key: "lost", label: "Lost", color: "bg-slate-200 text-slate-500" },
];
const STAGE_META = Object.fromEntries(STAGES.map((s) => [s.key, s]));

const inp = "mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm";
const lbl = "text-xs font-bold uppercase tracking-wider text-slate-500";

const money = (n) => "£" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

// ---------- Portal login page --------------------------------------------

export function PortalLogin() {
  useSeo({ title: "Sales Consultant Portal — Sign in", noindex: true });
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      if (data.role !== "sales_consultant" && data.role !== "admin") {
        toast.error("This login is for sales consultants only. Use your member login instead.");
        setBusy(false);
        return;
      }
      setUser(data);
      navigate("/portal/dashboard", { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10 bg-slate-50">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 text-emerald-700 font-bold text-xs uppercase tracking-widest mb-1">
          <Target size={14} /> Enterprise Sales Portal
        </div>
        <h1 className="font-display font-bold text-2xl">Sign in</h1>
        <p className="text-sm text-slate-500 mt-1">For registered sales consultants. If you're a member, use the standard <Link to="/login" className="underline">member login</Link>.</p>
        <form className="space-y-3 mt-5" onSubmit={submit} data-testid="portal-login-form">
          <label className="block">
            <span className={lbl}>Email</span>
            <input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inp} data-testid="portal-email" />
          </label>
          <label className="block">
            <span className={lbl}>Password</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inp} data-testid="portal-password" />
          </label>
          <button type="submit" disabled={busy} data-testid="portal-login-btn"
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm py-2.5 rounded-lg transition-colors disabled:opacity-50">
            {busy ? "Signing in…" : "Sign in to portal"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------- Portal layout (sidebar + outlet) ----------------------------

export function PortalLayout() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    navigate("/portal/login", { replace: true });
  };

  const nav = [
    { to: "/portal/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/portal/deals", icon: Briefcase, label: "My Deals" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10" data-testid="portal-shell">
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside className="bg-white border border-slate-200 rounded-xl p-3 h-fit lg:sticky lg:top-24">
          <div className="px-2 pb-2 mb-2 border-b border-slate-100">
            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Sales Portal</p>
            <p className="font-bold text-sm truncate">{user?.name || user?.email}</p>
          </div>
          <nav className="space-y-1">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold transition-colors ${isActive ? "bg-emerald-50 text-emerald-800" : "text-slate-600 hover:bg-slate-50"}`}>
                <n.icon size={15} /> {n.label}
              </NavLink>
            ))}
            <button onClick={logout} data-testid="portal-logout"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-rose-700 hover:bg-rose-50 transition-colors mt-2">
              <LogOut size={15} /> Sign out
            </button>
          </nav>
        </aside>
        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ---------- Dashboard ----------------------------------------------------

export function PortalDashboard() {
  useSeo({ title: "Portal — Dashboard", noindex: true });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/dashboard").then(({ data }) => setStats(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;
  if (!stats) return null;

  const kpis = [
    { label: `Won deals (${stats.year} YTD)`, value: stats.deals_won_ytd, sub: `${stats.deals_won} all time` },
    { label: `Revenue YTD`, value: money(stats.revenue_ytd_gbp) },
    { label: `Commission YTD`, value: money(stats.commission_ytd_gbp) },
    { label: `Open pipeline`, value: money(stats.pipeline_value_gbp), sub: `${stats.pipeline_count} active deals` },
  ];

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div>
        <h1 className="font-display font-bold text-2xl">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Your year to date and open pipeline.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{k.label}</p>
            <p className="font-display font-bold text-2xl mt-1">{k.value}</p>
            {k.sub && <p className="text-[11px] text-slate-400 mt-0.5">{k.sub}</p>}
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-bold text-base mb-3">Pipeline by stage</h2>
        <div className="space-y-2">
          {STAGES.map((s) => {
            const n = stats.by_stage[s.key] || 0;
            const w = stats.deals_total ? (n / stats.deals_total) * 100 : 0;
            return (
              <div key={s.key} className="flex items-center gap-3" data-testid={`stage-row-${s.key}`}>
                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded w-32 text-center ${s.color}`}>{s.label}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded">
                  <div className="h-2 bg-emerald-500 rounded" style={{ width: `${w}%` }} />
                </div>
                <span className="text-xs font-bold w-6 text-right">{n}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Deals list + create/edit modal -----------------------------

function DealModal({ deal, tiers, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: deal?.company_name || "",
    contact_name: deal?.contact_name || "",
    contact_email: deal?.contact_email || "",
    contact_phone: deal?.contact_phone || "",
    tier_slug: deal?.tier_slug || "",
    seats: deal?.seats || 0,
    value_gbp: deal?.value_gbp || 0,
    stage: deal?.stage || "new",
    expected_close_date: deal?.expected_close_date || "",
    notes: deal?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const chooseTier = (slug) => {
    const t = tiers.find((x) => x.slug === slug);
    setForm((f) => ({
      ...f, tier_slug: slug,
      seats: t?.seats || f.seats,
      value_gbp: t?.annual_price_gbp ? t.annual_price_gbp + (t.setup_fee_gbp || 0) : f.value_gbp,
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setBusy(true);
    try {
      if (deal?.id) await api.put(`/portal/deals/${deal.id}`, form);
      else await api.post("/portal/deals", form);
      toast.success(deal?.id ? "Deal updated" : "Deal added");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
        className="bg-white rounded-2xl p-6 w-full max-w-2xl my-6 space-y-3" data-testid="deal-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{deal?.id ? "Edit deal" : "Register a new deal"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label><span className={lbl}>Company name</span>
            <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={inp} data-testid="deal-company" /></label>
          <label><span className={lbl}>Contact name</span>
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inp} data-testid="deal-contact-name" /></label>
          <label><span className={lbl}>Contact email</span>
            <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inp} data-testid="deal-contact-email" /></label>
          <label><span className={lbl}>Contact phone</span>
            <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inp} data-testid="deal-contact-phone" /></label>
          <label><span className={lbl}>Pricing tier</span>
            <select value={form.tier_slug} onChange={(e) => chooseTier(e.target.value)} className={inp} data-testid="deal-tier">
              <option value="">—</option>
              {tiers.map((t) => <option key={t.slug} value={t.slug}>{t.name} — {t.seats} seats · {money(t.annual_price_gbp)}/yr</option>)}
            </select></label>
          <label><span className={lbl}>Seats</span>
            <input type="number" min="0" value={form.seats} onChange={(e) => setForm({ ...form, seats: Number(e.target.value) || 0 })} className={inp} data-testid="deal-seats" /></label>
          <label><span className={lbl}>Deal value (£)</span>
            <input type="number" min="0" step="50" value={form.value_gbp} onChange={(e) => setForm({ ...form, value_gbp: Number(e.target.value) || 0 })} className={inp} data-testid="deal-value" /></label>
          <label><span className={lbl}>Stage</span>
            <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className={inp} data-testid="deal-stage">
              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select></label>
          <label><span className={lbl}>Expected close date</span>
            <input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} className={inp} data-testid="deal-close-date" /></label>
        </div>
        <label className="block"><span className={lbl}>Notes</span>
          <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inp} data-testid="deal-notes" /></label>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="font-bold text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={busy} data-testid="deal-save"
            className="font-bold text-sm px-5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy ? "Saving…" : deal?.id ? "Update deal" : "Save deal"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function PortalDeals() {
  useSeo({ title: "Portal — My Deals", noindex: true });
  const [deals, setDeals] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stageFilter, setStageFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [dl, tl] = await Promise.all([
        api.get("/portal/deals", { params: stageFilter ? { stage: stageFilter } : {} }),
        api.get("/sales/tiers"),
      ]);
      setDeals(dl.data); setTiers(tl.data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stageFilter]);

  const remove = async (d) => {
    if (!window.confirm(`Delete deal "${d.company_name}"? This cannot be undone.`)) return;
    try { await api.delete(`/portal/deals/${d.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="portal-deals">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl">My Deals</h1>
          <p className="text-sm text-slate-500 mt-1">Pipeline of deals you've registered. Values auto-fill from your chosen tier.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="deals-new-btn"
          className="inline-flex items-center gap-1.5 bg-emerald-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-emerald-700">
          <Plus size={14} /> Register new deal
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setStageFilter("")} data-testid="stage-filter-all"
          className={`text-xs font-bold px-3 py-1.5 rounded-full border ${!stageFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>All</button>
        {STAGES.map((s) => (
          <button key={s.key} onClick={() => setStageFilter(s.key)} data-testid={`stage-filter-${s.key}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${stageFilter === s.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {loading ? <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div> :
        deals.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500">
            No deals yet{stageFilter ? ` in "${STAGE_META[stageFilter]?.label}"` : ""}. Click <strong>Register new deal</strong> to add one.
          </div>
        ) : (
          <ul className="space-y-2">
            {deals.map((d) => {
              const meta = STAGE_META[d.stage] || STAGES[0];
              const commission = (d.value_gbp * d.commission_percent) / 100;
              return (
                <li key={d.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid={`deal-row-${d.id}`}>
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{d.company_name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {d.contact_name || "—"}{d.contact_email && ` · ${d.contact_email}`}
                      {d.tier_slug && ` · ${d.tier_slug} tier · ${d.seats} seats`}
                      {d.expected_close_date && ` · close ${d.expected_close_date}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-bold text-base">{money(d.value_gbp)}</p>
                    <p className="text-[11px] text-emerald-700">+{money(commission)} commission</p>
                  </div>
                  <button onClick={() => setEditing(d)} data-testid={`deal-edit-${d.id}`}
                    className="text-xs font-bold text-slate-600 hover:text-slate-900 border border-slate-200 px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 hover:bg-slate-50"><Pencil size={11}/> Edit</button>
                  <button onClick={() => remove(d)} data-testid={`deal-delete-${d.id}`}
                    className="text-xs font-bold text-rose-600 hover:text-rose-800 border border-rose-200 px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 hover:bg-rose-50"><Trash2 size={11}/></button>
                </li>
              );
            })}
          </ul>
        )}

      {(showNew || editing) && (
        <DealModal
          deal={editing}
          tiers={tiers}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Admin: master pricing tiers --------------------------------

function TierModal({ tier, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: tier?.name || "", slug: tier?.slug || "",
    seats: tier?.seats || 0, annual_price_gbp: tier?.annual_price_gbp || 0,
    monthly_price_gbp: tier?.monthly_price_gbp || 0, setup_fee_gbp: tier?.setup_fee_gbp || 0,
    notes: tier?.notes || "", active: tier?.active !== false, display_order: tier?.display_order || 0,
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) { toast.error("Name and slug are required"); return; }
    setBusy(true);
    try {
      if (tier?.id) await api.put(`/admin/sales/pricing/${tier.id}`, form);
      else await api.post("/admin/sales/pricing", form);
      toast.success(tier?.id ? "Tier updated" : "Tier added"); onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
        className="bg-white rounded-2xl p-6 w-full max-w-xl my-6 space-y-3" data-testid="tier-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{tier?.id ? "Edit tier" : "New pricing tier"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label><span className={lbl}>Name</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="tier-name" /></label>
          <label><span className={lbl}>Slug (lowercase, no spaces)</span>
            <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} className={inp} data-testid="tier-slug" /></label>
          <label><span className={lbl}>Seats</span>
            <input type="number" min="0" value={form.seats} onChange={(e) => setForm({ ...form, seats: Number(e.target.value) || 0 })} className={inp} data-testid="tier-seats" /></label>
          <label><span className={lbl}>Annual price (£)</span>
            <input type="number" min="0" step="10" value={form.annual_price_gbp} onChange={(e) => setForm({ ...form, annual_price_gbp: Number(e.target.value) || 0 })} className={inp} data-testid="tier-annual" /></label>
          <label><span className={lbl}>Monthly price (£)</span>
            <input type="number" min="0" step="10" value={form.monthly_price_gbp} onChange={(e) => setForm({ ...form, monthly_price_gbp: Number(e.target.value) || 0 })} className={inp} /></label>
          <label><span className={lbl}>Setup fee (£)</span>
            <input type="number" min="0" step="10" value={form.setup_fee_gbp} onChange={(e) => setForm({ ...form, setup_fee_gbp: Number(e.target.value) || 0 })} className={inp} /></label>
          <label><span className={lbl}>Display order</span>
            <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) || 0 })} className={inp} /></label>
          <label className="flex items-end gap-2 pb-1">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-emerald-600" data-testid="tier-active" />
            <span className="text-sm font-bold">Active (visible to reps)</span>
          </label>
        </div>
        <label className="block"><span className={lbl}>Notes (shown to reps when choosing a tier)</span>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inp} /></label>
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" onClick={onClose} className="font-bold text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={busy} data-testid="tier-save"
            className="font-bold text-sm px-5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function AdminSalesPricing() {
  useSeo({ title: "Admin — Sales Pricing Tiers", noindex: true });
  const [tiers, setTiers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/sales/pricing"); setTiers(data); }
    catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" tier? Reps will no longer be able to pick it.`)) return;
    try { await api.delete(`/admin/sales/pricing/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-sales-pricing">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><PoundSterling size={18} /> Master pricing tiers</h2>
          <p className="text-sm text-slate-500 mt-1">Reps pick a tier when registering a deal. Only active tiers are shown to them.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="tier-new-btn"
          className="inline-flex items-center gap-1.5 bg-emerald-600 text-white font-bold text-sm px-4 py-2 rounded-lg hover:bg-emerald-700">
          <Plus size={14} /> New tier
        </button>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <ul className="space-y-2">
          {tiers.map((t) => (
            <li key={t.id} className={`bg-white border rounded-xl p-4 flex items-center gap-3 flex-wrap ${t.active ? "border-slate-200" : "border-slate-200 opacity-50"}`} data-testid={`tier-row-${t.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-bold">{t.name}</p>
                  <span className="font-mono text-[11px] text-slate-400">{t.slug}</span>
                  {!t.active && <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">inactive</span>}
                </div>
                <p className="text-xs text-slate-500">
                  {t.seats} seats · {money(t.annual_price_gbp)}/yr · {money(t.monthly_price_gbp)}/mo
                  {t.setup_fee_gbp > 0 && ` · ${money(t.setup_fee_gbp)} setup`}
                </p>
                {t.notes && <p className="text-[11px] text-slate-400 mt-0.5">{t.notes}</p>}
              </div>
              <button onClick={() => setEditing(t)} data-testid={`tier-edit-${t.slug}`}
                className="text-xs font-bold border border-slate-200 px-3 py-1.5 rounded-lg inline-flex items-center gap-1 hover:bg-slate-50"><Pencil size={11}/> Edit</button>
              <button onClick={() => remove(t)} data-testid={`tier-delete-${t.slug}`}
                className="text-xs font-bold text-rose-600 border border-rose-200 px-2 py-1.5 rounded-lg inline-flex items-center gap-1 hover:bg-rose-50"><Trash2 size={11}/></button>
            </li>
          ))}
        </ul>
      )}
      {(showNew || editing) && (
        <TierModal tier={editing} onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}
